import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import pluralize from 'pluralize';

import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import {
  UpdateOptimizationWorkloadRequest,
  WorkloadUpdatedResponse,
} from '@cast/types';

import { getWorkloadQueryKey } from 'hooks/queries/workload-optimization/useWorkloadQuery';
import { getWorkloadsQueryKey } from 'hooks/queries/workload-optimization/useWorkloadsQuery';

class UpdateWorkloadError extends Error {
  constructor(
    public readonly workload: UpdateOptimizationWorkloadRequest,
    public readonly error: AxiosError
  ) {
    super(
      `Failed to update workload: ${workload.workloadId}. Error: ${error.message}`
    );
  }
}

export class UpdateWorkloadsError extends Error {
  constructor(
    public readonly success: WorkloadUpdatedResponse[],
    public readonly errors: [UpdateWorkloadError, ...UpdateWorkloadError[]]
  ) {
    super(
      `Failed to update ${pluralize('workload', errors.length)}: ${errors
        .map(({ workload }) => workload.workloadId)
        .join(', ')}`
    );
  }

  hasFailedWorkloadId(id: string) {
    return this.errors.some((e) => e.workload.workloadId === id);
  }
}

const updateWorkload = async (workload: UpdateOptimizationWorkloadRequest) => {
  return await apiClient.workloadOptimization
    .updateWorkload(workload)
    .then((r) => r.data)
    .catch((error: AxiosError) => new UpdateWorkloadError(workload, error));
};

const updateWorkloads = async (
  workloads: UpdateOptimizationWorkloadRequest[]
) => {
  return Promise.all(workloads.map(updateWorkload));
};

export const useUpdateWorkloadMutation = ({
  enableInvalidateWorkloadQuery = true,
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      workload:
        | UpdateOptimizationWorkloadRequest[]
        | UpdateOptimizationWorkloadRequest
    ) => {
      if (Array.isArray(workload)) {
        const result = await updateWorkloads(workload);
        const errors = result.filter(
          (r: WorkloadUpdatedResponse | UpdateWorkloadError) =>
            r instanceof UpdateWorkloadError
        );

        if (errors.length) {
          const success = result.filter(
            (r: WorkloadUpdatedResponse | UpdateWorkloadError) =>
              !(r instanceof UpdateWorkloadError)
          ) as WorkloadUpdatedResponse[];
          throw new UpdateWorkloadsError(
            success,
            errors as [UpdateWorkloadError, ...UpdateWorkloadError[]]
          );
        }

        return result as WorkloadUpdatedResponse[];
      }
      const result = await updateWorkload(workload);
      if (result instanceof UpdateWorkloadError) {
        throw result;
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getWorkloadsQueryKey(),
      });
      // TODO: remove this flag and use the PATCH endpoint instead https://castai.atlassian.net/browse/UI-4607
      if (enableInvalidateWorkloadQuery) {
        queryClient.invalidateQueries({ queryKey: getWorkloadQueryKey() });
      }
    },
    onError: (error: UpdateWorkloadsError | UpdateWorkloadError) => {
      if (error instanceof UpdateWorkloadsError && error.success.length) {
        queryClient.invalidateQueries({
          queryKey: getWorkloadsQueryKey(),
        });
        queryClient.invalidateQueries({ queryKey: getWorkloadQueryKey() });
      }
      notify.error(error.message, { key: 'failed-to-update' });
    },
  });
};
